import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { kr } from '../lib/format';
import { PageHeader, Card } from '../components/ui';
import { RefreshCw, Landmark, Calendar, Copy, AlertTriangle, CheckCircle2 } from 'lucide-react';

const STATUS_LABEL = {
  ingen_lonn: { tekst: 'Ingen lønn i perioden', klasse: 'badge-info' },
  kommende:   { tekst: 'Kommende',              klasse: 'badge-info' },
  forfalt:    { tekst: 'Forfalt',               klasse: 'badge-danger' },
};

const SkattAgaPage = () => {
  const [aar, setAar] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [kopiert, setKopiert] = useState('');

  const last = () => {
    setData(null);
    api.get(`/skatt-aga/forfall?aar=${aar}`).then((r) => setData(r.data));
  };
  useEffect(() => { last(); /* eslint-disable-next-line */ }, [aar]);

  const kopier = (tekst, label) => {
    navigator.clipboard?.writeText(tekst).then(() => {
      setKopiert(label);
      setTimeout(() => setKopiert(''), 1500);
    });
  };

  return (
    <div>
      <PageHeader
        title="Skatt og arbeidsgiveravgift"
        subtitle="Forfallsterminer for skattetrekk + AGA og forskuddsskatt næringsdrivende."
        actions={
          <button className="btn-ghost" onClick={last}>
            <RefreshCw size={14} /> Oppdater
          </button>
        }
      />

      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontWeight: 600 }}>År:</label>
          <select value={aar} onChange={(e) => setAar(Number(e.target.value))}
                  style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)' }}>
            {[0, -1, -2, 1].map((d) => {
              const v = new Date().getFullYear() + d;
              return <option key={v} value={v}>{v}</option>;
            })}
          </select>
          {data && (
            <span className="fm-text-muted" style={{ marginLeft: 'auto' }}>
              Orgnr {data.orgnr} · konto {data.kontonr_default}
            </span>
          )}
        </div>
      </Card>

      {!data && <Card><span className="fm-text-muted">Laster…</span></Card>}

      {data && (
        <>
          <h3 style={{ marginTop: 24, marginBottom: 12 }}>Skattetrekk + arbeidsgiveravgift</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
            {data.skattetrekk_aga.map((t) => {
              const st = STATUS_LABEL[t.status] || STATUS_LABEL.kommende;
              const erForfalt = t.status === 'forfalt' && t.total > 0;
              return (
                <Card key={t.termin} style={{
                  borderLeft: erForfalt ? '4px solid var(--danger, #dc2626)' :
                              (t.status === 'kommende' && t.total > 0 && t.dager_til_frist <= 14
                                ? '4px solid var(--theme-accent)' : '4px solid transparent'),
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 16 }}>
                        Termin {t.termin}/{t.aar}
                      </div>
                      <div className="fm-text-muted" style={{ fontSize: 13 }}>
                        {t.fra} – {t.til}
                      </div>
                    </div>
                    <span className={`badge ${st.klasse}`}>{st.tekst}</span>
                  </div>

                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginTop: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span>Skattetrekk</span><strong>{kr(t.skattetrekk)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span>Arbeidsgiveravgift</span><strong>{kr(t.aga)}</strong>
                    </div>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', padding: '8px 0',
                      borderTop: '1px solid var(--border)', marginTop: 4, fontSize: 16,
                    }}>
                      <strong>Totalt å betale</strong><strong>{kr(t.total)}</strong>
                    </div>
                  </div>

                  {t.total > 0 && (
                    <div style={{ marginTop: 12, fontSize: 13, background: 'var(--bg-subtle, #f9fafb)', padding: 10, borderRadius: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0' }}>
                        <span className="fm-text-muted">Frist:</span>
                        <span><Calendar size={12} style={{ verticalAlign: -2 }} /> {t.frist}
                          {t.dager_til_frist >= 0 && t.dager_til_frist <= 14 &&
                            <span style={{ color: 'var(--theme-accent)', marginLeft: 6 }}>
                              ({t.dager_til_frist} dager)
                            </span>}
                          {t.dager_til_frist < 0 &&
                            <span style={{ color: 'var(--danger, #dc2626)', marginLeft: 6 }}>
                              (forfalt {-t.dager_til_frist} dager)
                            </span>}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0' }}>
                        <span className="fm-text-muted">Konto:</span>
                        <span style={{ fontFamily: 'monospace', cursor: 'pointer' }}
                              onClick={() => kopier(t.kontonr, `konto-${t.termin}`)}>
                          {t.kontonr} <Copy size={11} style={{ verticalAlign: -1, opacity: 0.5 }} />
                          {kopiert === `konto-${t.termin}` && <span style={{ color: 'var(--success, #16a34a)', marginLeft: 4, fontSize: 11 }}>kopiert!</span>}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0' }}>
                        <span className="fm-text-muted">KID:</span>
                        <span style={{ fontFamily: 'monospace', cursor: 'pointer' }}
                              onClick={() => kopier(t.kid, `kid-${t.termin}`)}>
                          {t.kid} <Copy size={11} style={{ verticalAlign: -1, opacity: 0.5 }} />
                          {kopiert === `kid-${t.termin}` && <span style={{ color: 'var(--success, #16a34a)', marginLeft: 4, fontSize: 11 }}>kopiert!</span>}
                        </span>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          <h3 style={{ marginTop: 32, marginBottom: 12 }}>Forskuddsskatt næringsdrivende</h3>
          <Card>
            <p className="fm-text-muted" style={{ marginTop: 0 }}>
              Forskuddsskatt fastsettes av Skatteetaten. Beløp og KID hentes fra
              skatteetaten.no eller via eFaktura/avtalegiro. Frister 2026:
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: 8 }}>Termin</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Frist</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.forskuddsskatt.map((f) => (
                  <tr key={f.termin} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: 8 }}>{f.termin}. termin</td>
                    <td style={{ padding: 8 }}>{f.frist}</td>
                    <td style={{ padding: 8 }}>
                      {f.dager_til_frist < 0 ? (
                        <span className="badge badge-danger">
                          <AlertTriangle size={12} /> Forfalt
                        </span>
                      ) : f.dager_til_frist <= 14 ? (
                        <span className="badge badge-warning">
                          {f.dager_til_frist} dager igjen
                        </span>
                      ) : (
                        <span className="badge badge-info">
                          <CheckCircle2 size={12} /> Senere
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
};

export default SkattAgaPage;
